import { useMemo, useState } from "react";
import { Search, ArrowLeft, X, ChevronRight } from "lucide-react";

export const MENU_CATEGORIES = ["Starters", "Mains", "Desserts", "Beverages", "Breads", "Sides", "Combos"];
const title = (c) => (c || "Other").toLowerCase().replace(/\b\w/g, (x) => x.toUpperCase());

export function groupByCategory(recipes, keepEmpty = true) {
  const g = new Map(keepEmpty ? MENU_CATEGORIES.map((c) => [c.toUpperCase(), { key: c.toUpperCase(), label: c, items: [] }]) : []);
  for (const r of recipes) {
    const k = (r.category || "Other").trim().toUpperCase();
    if (!g.has(k)) g.set(k, { key: k, label: title(k), items: [] });
    g.get(k).items.push(r);
  }
  return [...g.values()];
}

// Search box + category cards → dishes of one category. `render(list)` draws the dishes.
export function CategoryBrowser({ recipes, render, tid = "menu", keepEmpty = true, placeholder = "Search dishes by name" }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(null);
  const [focus, setFocus] = useState(false);
  const groups = useMemo(() => groupByCategory(recipes, keepEmpty), [recipes, keepEmpty]);
  const term = q.trim().toLowerCase();
  const hits = useMemo(() => {
    if (!term) return [];
    const starts = recipes.filter((r) => r.name.toLowerCase().startsWith(term));
    return [...starts, ...recipes.filter((r) => !starts.includes(r) && r.name.toLowerCase().includes(term))];
  }, [recipes, term]);
  const open = groups.find((g) => g.key === cat);
  return (
    <div className="cb" data-testid={`${tid}-browser`}>
      <div className="cb-search">
        <Search size={15} />
        <input value={q} onChange={(e) => { setQ(e.target.value); setFocus(true); }} onKeyDown={(e) => { if (e.key === "Escape") setFocus(false); if (e.key === "Enter") { e.preventDefault(); setFocus(false); } }} onFocus={() => setFocus(true)} onBlur={() => setTimeout(() => setFocus(false), 150)} placeholder={placeholder} data-testid={`${tid}-search`} />
        {q && <button type="button" onClick={() => setQ("")} aria-label="Clear search" data-testid={`${tid}-search-clear`}><X size={14} /></button>}
        {focus && hits.length > 0 && (
          <ul className="cb-suggest" data-testid={`${tid}-suggestions`}>
            {hits.slice(0, 6).map((r) => (
              <li key={r.id}><button type="button" onMouseDown={() => { setQ(r.name); setFocus(false); }} data-testid={`${tid}-suggest-${r.id}`}><b>{r.name}</b><span>{title(r.category)}</span></button></li>
            ))}
          </ul>
        )}
      </div>
      {term ? (
        <div data-testid={`${tid}-search-results`}>
          <div className="cb-bar"><span className="overline">{hits.length} result{hits.length === 1 ? "" : "s"} for “{q.trim()}” across all categories</span></div>
          {hits.length ? render(hits) : <div className="empty small" data-testid={`${tid}-no-results`}>No dish matches “{q.trim()}”.</div>}
        </div>
      ) : open ? (
        <div data-testid={`${tid}-category-view`}>
          <div className="cb-bar">
            <button type="button" className="secondary-button" onClick={() => setCat(null)} data-testid={`${tid}-back-to-categories`}><ArrowLeft size={14} /> Back to Categories</button>
            <span className="overline" data-testid={`${tid}-category-title`}>{open.label} · {open.items.length} item{open.items.length === 1 ? "" : "s"}</span>
          </div>
          {open.items.length ? render(open.items) : <div className="empty small">No items in {open.label} yet.</div>}
        </div>
      ) : (
        <div className="cb-cards" data-testid={`${tid}-categories`}>
          {groups.map((g, i) => (
            <button type="button" key={g.key} className="cb-card" style={{ animationDelay: `${i * 30}ms` }} onClick={() => setCat(g.key)} data-testid={`${tid}-cat-${g.key}`}>
              <b>{g.label}</b><span data-testid={`${tid}-cat-count-${g.key}`}>{g.items.length} item{g.items.length === 1 ? "" : "s"}</span><ChevronRight size={16} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
